<template>
  <div 
    class="toast-notification"
    :class="toastClasses"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
  >
    <div class="flex">
      <!-- Icon -->
      <div class="flex-shrink-0">
        <component :is="iconComponent" class="w-5 h-5" aria-hidden="true" />
      </div>

      <!-- Content -->
      <div class="ml-3 flex-1">
        <p v-if="toast.title" class="text-sm font-medium" :class="titleClasses">
          {{ toast.title }}
        </p>
        <p class="text-sm" :class="messageClasses">
          {{ toast.message }}
        </p>
      </div>

      <!-- Close button -->
      <div class="ml-4 flex-shrink-0 flex">
        <button
          @click="$emit('close')"
          class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md"
          :class="closeButtonClasses"
        >
          <span class="sr-only">Close</span>
          <XMarkIcon class="w-5 h-5" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- Progress bar (if duration is set) -->
    <div 
      v-if="toast.duration > 0" 
      class="absolute bottom-0 left-0 h-1 bg-current opacity-20 transition-all ease-linear"
      :style="{ width: progressWidth + '%', transitionDuration: toast.duration + 'ms' }"
    ></div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  toast: {
    type: Object,
    required: true,
    validator: (toast) => {
      return typeof toast.message === 'string' && 
             ['success', 'error', 'warning', 'info'].includes(toast.type)
    }
  }
})

// Emits
defineEmits(['close'])

// Progress bar animation
const progressWidth = ref(100)

onMounted(() => {
  if (props.toast.duration > 0) {
    // Start progress bar animation
    requestAnimationFrame(() => {
      progressWidth.value = 0
    })
  }
})

// Computed properties
const iconComponent = computed(() => {
  const icons = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon
  }
  return icons[props.toast.type] || InformationCircleIcon
})

const toastClasses = computed(() => {
  const baseClasses = [
    'toast-notification',
    'relative',
    'max-w-sm',
    'w-full',
    'rounded-lg',
    'shadow-elevation-3',
    'border',
    'p-4',
    'transition-all',
    'duration-300',
    'overflow-hidden'
  ]

  const typeClasses = {
    success: [
      'bg-success-50', 'border-success-200', 'text-success-800',
      'dark:bg-success-900/20', 'dark:border-success-700', 'dark:text-success-200'
    ],
    error: [
      'bg-danger-50', 'border-danger-200', 'text-danger-800',
      'dark:bg-danger-900/20', 'dark:border-danger-700', 'dark:text-danger-200'
    ],
    warning: [
      'bg-warning-50', 'border-warning-200', 'text-warning-800',
      'dark:bg-warning-900/20', 'dark:border-warning-700', 'dark:text-warning-200'
    ],
    info: [
      'bg-primary-50', 'border-primary-200', 'text-primary-800',
      'dark:bg-primary-900/20', 'dark:border-primary-700', 'dark:text-primary-200'
    ]
  }

  return [...baseClasses, ...typeClasses[props.toast.type]]
})

const titleClasses = computed(() => {
  const classes = {
    success: 'text-success-900 dark:text-success-100',
    error: 'text-danger-900 dark:text-danger-100',
    warning: 'text-warning-900 dark:text-warning-100',
    info: 'text-primary-900 dark:text-primary-100'
  }
  return classes[props.toast.type]
})

const messageClasses = computed(() => {
  const classes = {
    success: 'text-success-700 dark:text-success-200',
    error: 'text-danger-700 dark:text-danger-200',
    warning: 'text-warning-700 dark:text-warning-200',
    info: 'text-primary-700 dark:text-primary-200'
  }
  return classes[props.toast.type]
})

const closeButtonClasses = computed(() => {
  const classes = {
    success: 'text-success-500 hover:text-success-600 focus:ring-success-500',
    error: 'text-danger-500 hover:text-danger-600 focus:ring-danger-500',
    warning: 'text-warning-500 hover:text-warning-600 focus:ring-warning-500',
    info: 'text-primary-500 hover:text-primary-600 focus:ring-primary-500'
  }
  return classes[props.toast.type]
})
</script>

<style scoped>
.toast-notification {
  backdrop-filter: blur(8px);
}
</style>